import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { setAuthToken } from "../lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("yopinion_token"));
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await api.get("/users/me");
      setUser(response.data);
    } catch (error) {
      setUser(null);
      setToken(null);
      localStorage.removeItem("yopinion_token");
      setAuthToken(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      setAuthToken(token);
      fetchMe();
    } else {
      setAuthToken(null);
      setLoading(false);
    }
  }, [token, fetchMe]);

  const login = async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    setAuthToken(response.data.token);
    setToken(response.data.token);
    localStorage.setItem("yopinion_token", response.data.token);
    setUser(response.data.user);
    toast.success("Welcome back.");
    return response.data.user;
  };

  const signup = async (payload) => {
    const response = await api.post("/auth/register", payload);
    setAuthToken(response.data.token);
    setToken(response.data.token);
    localStorage.setItem("yopinion_token", response.data.token);
    setUser(response.data.user);
    toast.success("Your account is ready.");
    return response.data.user;
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("yopinion_token");
    setAuthToken(null);
    toast.message("You’ve been signed out.");
  };

  const completeOAuthLogin = useCallback((authToken, userProfile) => {
    setAuthToken(authToken);
    setToken(authToken);
    localStorage.setItem("yopinion_token", authToken);
    setUser(userProfile);
    toast.success("Signed in with Google.");
  }, []);

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      login,
      signup,
      logout,
      completeOAuthLogin,
      refreshUser: fetchMe,
      setUser,
    }),
    [user, token, loading, fetchMe, completeOAuthLogin]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
